import { useState } from 'react'
import { useIonRouter } from '@ionic/react'
import parsePhoneNumberFromString from 'libphonenumber-js'

import { useCheckInActions } from './check-in-store'
import { useToast } from '../contexts/toastContext'
import { useIsAdminRoute } from '../hooks/routing'
import { useOptInClient } from '../marketing/customer-opt-in/mutations'
import { emailCheckIn, getClientFromE164 } from '../rotate/api'
import { useCheckIn } from './paired/mutations'

import type { CountryCode } from 'libphonenumber-js'

type UseCheckInProps = {
  addCustomerRedirect: string
  loyaltyRedirect: string
}

function useEmailCheckIn({
  addCustomerRedirect,
  loyaltyRedirect,
}: UseCheckInProps) {
  const [isLoading, setIsLoading] = useState(false)

  const { resetClientInfo, setCheckInMethod, setClientInfo } =
    useCheckInActions()
  const checkInClient = useCheckIn()

  const router = useIonRouter()
  const setToast = useToast()
  const isAdminRoute = useIsAdminRoute()

  function redirectToLoyalty(url: string) {
    router.push(url, 'forward', 'pop')
  }

  function redirectToAddCustomer() {
    router.push(addCustomerRedirect, 'forward', 'pop')
  }

  async function handleCheckIn({ email }: { email: string }) {
    setIsLoading(true)
    setCheckInMethod({ email })

    try {
      const { client } = await emailCheckIn(email)
      setClientInfo({ email })

      if (!client) return redirectToAddCustomer()

      const phoneNumber = client.phoneNumbers?.length
        ? client.phoneNumbers[0].phoneNumber
        : ''

      setClientInfo({
        ...client,
        dobObject: {
          month: client.dobObject.month ?? null,
          day: client.dobObject.day ?? null,
          year: client.dobObject.year ?? null,
        },
        phoneNumber,
      })

      if (!isAdminRoute)
        return router.push(
          `${loyaltyRedirect}?clientId=${client.objectId}`,
          'none',
          'replace'
        )

      // check-in client (if admin route then it is cashier)
      checkInClient.mutate(
        {
          checkInMethod: { phone: phoneNumber },
          clientId: client.objectId,
          type: 'pos',
        },
        {
          onSuccess: (data) => {
            console.timeEnd('checkin')
            // redirect to cashier loyalty
            redirectToLoyalty(
              `${loyaltyRedirect}?clientId=${client.objectId}&stationClientId=${data.stationClient.objectId}`
            )
          },
        }
      )
    } catch (e) {
      console.error(e)
      resetClientInfo()
      const errorMessage = e instanceof Error ? e.message : 'Unknown Error.'

      setToast({ color: 'danger', message: errorMessage })
    } finally {
      setIsLoading(false)
    }
  }

  return { handleCheckIn, isLoading }
}

function usePhoneCheckIn({
  addCustomerRedirect,
  loyaltyRedirect,
}: UseCheckInProps) {
  const [isLoading, setisLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { resetClientInfo, setClientInfo, setCheckInMethod } =
    useCheckInActions()

  const router = useIonRouter()
  const setToast = useToast()
  const optInClient = useOptInClient()
  const checkInClient = useCheckIn()

  function redirectToLoyalty(url: string) {
    router.push(url, 'forward', 'pop')
  }

  function redirectToAddCustomer() {
    router.push(addCustomerRedirect, 'forward', 'pop')
  }

  async function handleCheckIn({
    autoOptIn,
    phoneNumber,
    country,
  }: {
    autoOptIn?: boolean
    phoneNumber: string
    country: CountryCode
  }) {
    const parsedNumber = parsePhoneNumberFromString(phoneNumber, country)
    if (!parsedNumber?.isValid())
      return setToast({
        message: 'You have entered an invalid phone number.',
        color: 'danger',
      })
    const e164 = parsedNumber.number
    setisLoading(true)
    setCheckInMethod({ phone: e164.toString() })

    // CHECK IN E164
    try {
      const response = await getClientFromE164(e164)
      setClientInfo({ phoneNumber: e164.toString() })
      const possibleClient = response.client

      if (possibleClient) {
        const { phoneNumbers, ...clientInfo } = possibleClient
        if (autoOptIn) {
          optInClient.mutate(e164, {
            onSuccess: () => {
              setClientInfo({
                ...clientInfo,
                dobObject: {
                  month: clientInfo.dobObject.month ?? null,
                  day: clientInfo.dobObject.day ?? null,
                  year: clientInfo.dobObject.year ?? null,
                },
                phoneNumber,
              })
              // redirect to customer loyalty where we check in customer
              return redirectToLoyalty(
                `${loyaltyRedirect}?clientId=${clientInfo.objectId}`
              )
            },
          })
        } else {
          setClientInfo({
            ...clientInfo,
            dobObject: {
              month: clientInfo.dobObject.month ?? null,
              day: clientInfo.dobObject.day ?? null,
              year: clientInfo.dobObject.year ?? null,
            },
            phoneNumber: e164.toString(),
          })
          // check-in client (if not auto opting in then it is cashier)
          checkInClient.mutate(
            {
              checkInMethod: { phone: e164.toString() },
              clientId: clientInfo.objectId,
              type: 'pos',
            },
            {
              onSuccess: (data) =>
                // redirect to cashier loyalty
                redirectToLoyalty(
                  `${loyaltyRedirect}?clientId=${clientInfo.objectId}&stationClientId=${data.stationClient.objectId}`
                ),
            }
          )
        }
      } else {
        return redirectToAddCustomer()
      }
    } catch (error) {
      console.error(error)
      resetClientInfo()
      if (error instanceof Error) setError(error.message)
    } finally {
      setisLoading(false)
    }
  }

  return { error, handleCheckIn, isLoading }
}

export { useEmailCheckIn, usePhoneCheckIn }
